import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import './VideoCard.css';

const DEFAULT_EMBED_PARENTS = Object.freeze([
  'twitch-reels.vercel.app',
  'localhost',
  '127.0.0.1',
]);

const VISIBLE_THRESHOLD = 0.25;

/**
 * Deduplicates an array while filtering out falsy values.
 *
 * @param {Array<string|undefined|null>} values - Values to normalize.
 * @returns {string[]} Distinct, truthy values.
 */
const uniqueParents = (values) =>
  Array.from(new Set(values.filter((value) => Boolean(value && value.trim()))));

/**
 * Attempts to extract a Twitch clip slug from the provided URL.
 *
 * @param {string|undefined|null} candidate - Raw URL that may contain a clip identifier.
 * @returns {string|null} Extracted clip slug.
 */
const extractClipSlug = (candidate) => {
  if (!candidate) {
    return null;
  }

  try {
    const url = new URL(candidate);
    const clipParam = url.searchParams.get('clip');
    if (clipParam) {
      return clipParam;
    }

    const segments = url.pathname.split('/').filter(Boolean);
    if (!segments.length) {
      return null;
    }

    const clipIndex = segments.findIndex((segment) => segment.toLowerCase() === 'clip');
    if (clipIndex >= 0 && segments[clipIndex + 1]) {
      return segments[clipIndex + 1];
    }

    return segments[segments.length - 1] || null;
  } catch (error) {
    return null;
  }
};

/**
 * Builds a Twitch embed URL with the correct parent whitelist applied.
 *
 * @param {string|undefined|null} embedUrl - Preferred embed URL supplied by the API.
 * @param {string|undefined|null} twitchUrl - Canonical Twitch clip URL used as a fallback.
 * @param {string[]} parents - Allowed parent hostnames required by the Twitch embed.
 * @returns {string|null} Normalized embed URL.
 */
const buildEmbedSource = (embedUrl, twitchUrl, parents) => {
  let candidate = embedUrl || null;

  if (!candidate && twitchUrl) {
    const slug = extractClipSlug(twitchUrl);
    if (slug) {
      candidate = `https://clips.twitch.tv/embed?clip=${encodeURIComponent(slug)}`;
    }
  }

  if (!candidate) {
    return null;
  }

  try {
    const url = new URL(candidate);

    if (!url.searchParams.get('clip')) {
      const slug = extractClipSlug(candidate);
      if (slug) {
        url.searchParams.set('clip', slug);
      }
    }

    const parentValues = uniqueParents([
      ...url.searchParams.getAll('parent'),
      ...parents,
    ]);
    url.searchParams.delete('parent');
    parentValues.forEach((parent) => {
      url.searchParams.append('parent', parent);
    });

    if (!url.searchParams.has('autoplay')) {
      url.searchParams.set('autoplay', 'false');
    }
    if (!url.searchParams.has('muted')) {
      url.searchParams.set('muted', 'true');
    }

    return url.toString();
  } catch (error) {
    return null;
  }
};

/**
 * @typedef {object} VideoCardProps
 * @property {string|null} url - Source for the video clip.
 * @property {string|null} [embedUrl] - Twitch-supplied embed URL for the clip.
 * @property {string|null} [twitchUrl] - Canonical Twitch clip URL used for deep linking.
 * @property {string} channel - Channel name.
 * @property {string} [creatorName] - Creator display name.
 * @property {number} index - Index used for scroll snapping metadata.
 * @property {string} title - Video title for accessibility.
 * @property {string[]} [tags] - List of tags to render with the clip.
 * @property {string} [batchId] - Identifier for the current feed batch.
 * @property {number} [order] - Relative order within the current batch.
 * @property {boolean} [isUnavailable] - True when the media asset is unavailable.
 * @property {string|null} [unavailableReason] - Optional reason code for the unavailable state.
 */

/**
 * Displays a single reel inside the scroll-snapped list.
 *
 * @param {VideoCardProps} props - Component props.
 * @returns {JSX.Element} Reel card element.
 */
const VideoCard = forwardRef(function VideoCard({
  embedUrl,
  twitchUrl,
  channel,
  creatorName,
  avatarSrc: _avatarSrc,
  index,
  title,
  tags,
  isUnavailable,
  unavailableReason,
  viewCount: _viewCount,
  durationSeconds: _durationSeconds,
  language: _language,
  publishedAt: _publishedAt,
  batchId: _batchId,
  order: _order,
  ...articleProps
}, forwardedRef) {
  const fallbackChannel = channel?.trim() || creatorName?.trim() || 'Unknown creator';
  const clipTitle = title?.trim() || `${fallbackChannel} clip`;

  const embedParents = useMemo(() => {
    const runtimeParent =
      typeof window !== 'undefined' && window.location?.hostname
        ? window.location.hostname
        : null;

    return uniqueParents([...DEFAULT_EMBED_PARENTS, runtimeParent]);
  }, []);

  const embedSrc = useMemo(
    () => buildEmbedSource(embedUrl, twitchUrl, embedParents),
    [embedParents, embedUrl, twitchUrl],
  );

  const hasEmbed = Boolean(embedSrc) && !isUnavailable;

  const iframeRef = useRef(null);
  const cardRef = useRef(null);

  const setCardRef = useCallback(
    (node) => {
      cardRef.current = node;
      if (!forwardedRef) {
        return;
      }

      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
        return;
      }

      forwardedRef.current = node;
    },
    [forwardedRef],
  );

  useEffect(() => {
    const iframe = iframeRef.current;
    const cardNode = cardRef.current;

    if (!iframe) {
      return undefined;
    }

    let observer = null;
    let lastVisibility = null;

    const pauseClip = () => {
      const { contentWindow } = iframe;
      if (contentWindow && typeof contentWindow.postMessage === 'function') {
        try {
          contentWindow.postMessage({ event: 'command', func: 'pause' }, '*');
        } catch (error) {
          // Ignore cross-origin access issues when attempting to pause the clip.
        }
      }

      iframe.removeAttribute('src');
    };

    const ensureSource = () => {
      if (!embedSrc) {
        return;
      }

      if (iframe.getAttribute('src') !== embedSrc) {
        iframe.setAttribute('src', embedSrc);
      }
    };

    const updateVisibility = (isVisible) => {
      if (lastVisibility === isVisible) {
        return;
      }

      lastVisibility = isVisible;

      if (isVisible) {
        ensureSource();
      } else {
        pauseClip();
      }
    };

    if (!hasEmbed || !embedSrc) {
      pauseClip();
      return () => {
        if (observer) {
          observer.disconnect();
        }
        pauseClip();
      };
    }

    const computeInitialVisibility = () => {
      if (
        typeof window === 'undefined' ||
        !cardNode ||
        typeof cardNode.getBoundingClientRect !== 'function'
      ) {
        return true;
      }

      const rect = cardNode.getBoundingClientRect();

      if (rect.width === 0 && rect.height === 0) {
        return true;
      }

      const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
      const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;

      if (viewportHeight === 0 && viewportWidth === 0) {
        return true;
      }

      const intersectsHorizontally = rect.left < viewportWidth && rect.right > 0;
      const intersectsVertically = rect.top < viewportHeight && rect.bottom > 0;

      return intersectsHorizontally && intersectsVertically;
    };

    updateVisibility(computeInitialVisibility());

    if (cardNode && typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target !== cardNode) {
              return;
            }

            const isVisible =
              entry.isIntersecting && entry.intersectionRatio >= VISIBLE_THRESHOLD;
            updateVisibility(isVisible);
          });
        },
        { threshold: [VISIBLE_THRESHOLD, 0] },
      );

      observer.observe(cardNode);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }

      pauseClip();
    };
  }, [embedSrc, hasEmbed]);

  const normalizedTags = useMemo(() => {
    if (!Array.isArray(tags)) {
      return [];
    }

    const sanitized = tags
      .map((tag) => (typeof tag === 'string' ? tag.trim() : ''))
      .filter(Boolean);

    return Array.from(
      new Map(
        sanitized.map((tag) => {
          const cleanTag = tag.replace(/^#/u, '');
          const key = cleanTag.toLowerCase();
          return [key, cleanTag || tag];
        }),
      ).values(),
    );
  }, [tags]);

  const mediaStateLabel = useMemo(() => {
    if (hasEmbed) {
      return 'Embedded Twitch clip ready for playback.';
    }
    return 'Clip unavailable. Try another reel.';
  }, [hasEmbed]);

  const unavailableMessage = useMemo(() => {
    if (hasEmbed) {
      return null;
    }
    if (unavailableReason === 'not_found') {
      return 'Clip not found.';
    }
    return 'Clip unavailable. Try another reel.';
  }, [hasEmbed, unavailableReason]);

  return (
    <article
      className="videoCard"
      data-reel-index={index}
      role="listitem"
      ref={setCardRef}
      {...articleProps}
    >
      <div
        className={`videoCard__player${hasEmbed ? '' : ' videoCard__player--unavailable'}`}
      >
        {hasEmbed ? (
          <div className="videoCard__embedWrapper">
            <iframe
              className="videoCard__embed"
              title={clipTitle}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
              allowFullScreen
              loading="lazy"
              ref={iframeRef}
            />
            <span className="videoCard__mediaStatus sr-only">{mediaStateLabel}</span>
            {twitchUrl && (
              <p className="videoCard__embedAttribution">
                <a
                  href={twitchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="videoCard__embedLink"
                >
                  Watch on Twitch
                </a>
              </p>
            )}
          </div>
        ) : (
          <div className="videoCard__placeholder" role="status" aria-live="polite">
            <p className="videoCard__placeholderMessage">{unavailableMessage}</p>
          </div>
        )}
      </div>
      {normalizedTags.length > 0 && (
        <ul className="videoCard__tags" aria-label="Clip tags">
          {normalizedTags.map((tag) => (
            <li key={tag.toLowerCase()} className="videoCard__tag">
              #{tag}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
});

export default VideoCard;
